import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  FileText, 
  Plus, 
  Search,
  Download,
  RefreshCw,
  Calendar,
  DollarSign,
  FileCheck,
  FileMinus,
  FilePlus,
  Eye,
  Printer,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Share2,
  MessageCircle,
  Mail,
  Loader2,
  User,
  Building2,
  X,
  CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [afipStatus, setAfipStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Dialog states
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showCreditNoteDialog, setShowCreditNoteDialog] = useState(false);
  const [showDebitNoteDialog, setShowDebitNoteDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Form states
  const [invoiceForm, setInvoiceForm] = useState({
    importe_total: '',
    concepto: 1,
    doc_tipo: 'cf',
    doc_nro: '',
    descripcion: ''
  });
  
  const [creditNoteForm, setCreditNoteForm] = useState({
    importe_total: '',
    cbte_asoc_nro: '',
    motivo: ''
  });
  
  const [debitNoteForm, setDebitNoteForm] = useState({
    importe_total: '',
    cbte_asoc_nro: '',
    motivo: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [filterType, setFilterType] = useState('all');
  
  // Date filters
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  
  // Taxpayer lookup
  const [lookupCuit, setLookupCuit] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [taxpayerData, setTaxpayerData] = useState(null);
  
  // Share dialog
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [loadingShare, setLoadingShare] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  useEffect(() => {
    fetchAfipStatus();
    fetchInvoices();
    fetchSummary();
  }, [page, dateFrom, dateTo]);

  const fetchAfipStatus = async () => {
    try {
      const response = await axios.get(`${API}/afip/status`, { withCredentials: true });
      setAfipStatus(response.data);
    } catch (error) {
      console.error('Error fetching AFIP status:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      let url = `${API}/afip/invoices?page=${page}&limit=15`;
      
      // Add date filters
      if (dateFrom) {
        url += `&date_from=${format(dateFrom, 'yyyy-MM-dd')}`;
      }
      if (dateTo) {
        url += `&date_to=${format(dateTo, 'yyyy-MM-dd')}`;
      }
      
      const response = await axios.get(url, { withCredentials: true });
      setInvoices(response.data.invoices);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/afip/invoices/summary`, { withCredentials: true });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.importe_total) {
      toast.error('Ingrese el importe total');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/afip/invoice`, {
        importe_total: parseFloat(invoiceForm.importe_total),
        concepto: invoiceForm.concepto,
        doc_tipo: invoiceForm.doc_tipo,
        doc_nro: invoiceForm.doc_nro || 0,
        items: invoiceForm.descripcion ? [{
          descripcion: invoiceForm.descripcion,
          cantidad: 1,
          precio_unitario: parseFloat(invoiceForm.importe_total)
        }] : []
      }, { withCredentials: true });
      
      toast.success(`Factura C emitida - CAE: ${response.data.invoice.cae}`);
      setShowInvoiceDialog(false);
      setInvoiceForm({ importe_total: '', concepto: 1, doc_tipo: 'cf', doc_nro: '', descripcion: '' });
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al emitir factura');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCreditNote = async () => {
    if (!creditNoteForm.importe_total || !creditNoteForm.cbte_asoc_nro) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/afip/credit-note`, {
        importe_total: parseFloat(creditNoteForm.importe_total),
        cbte_asoc_tipo: 11,
        cbte_asoc_nro: parseInt(creditNoteForm.cbte_asoc_nro),
        motivo: creditNoteForm.motivo
      }, { withCredentials: true });
      
      toast.success(`Nota de Crédito emitida - CAE: ${response.data.credit_note.cae}`);
      setShowCreditNoteDialog(false);
      setCreditNoteForm({ importe_total: '', cbte_asoc_nro: '', motivo: '' });
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al emitir nota de crédito');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateDebitNote = async () => {
    if (!debitNoteForm.importe_total || !debitNoteForm.cbte_asoc_nro) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/afip/debit-note`, {
        importe_total: parseFloat(debitNoteForm.importe_total),
        cbte_asoc_tipo: 11,
        cbte_asoc_nro: parseInt(debitNoteForm.cbte_asoc_nro),
        motivo: debitNoteForm.motivo
      }, { withCredentials: true });
      
      toast.success(`Nota de Débito emitida - CAE: ${response.data.debit_note.cae}`);
      setShowDebitNoteDialog(false);
      setDebitNoteForm({ importe_total: '', cbte_asoc_nro: '', motivo: '' });
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al emitir nota de débito');
    } finally {
      setSubmitting(false);
    }
  };

  const viewInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailDialog(true);
  };

  // Lookup taxpayer by CUIT
  const handleLookupCuit = async () => {
    if (!lookupCuit || lookupCuit.length < 11) {
      toast.error('Ingrese un CUIT válido (11 dígitos)');
      return;
    }
    
    setLookingUp(true);
    setTaxpayerData(null);
    try {
      const response = await axios.post(`${API}/afip/taxpayer/lookup`, {
        cuit: lookupCuit.replace(/\D/g, '')
      }, { withCredentials: true });
      
      if (response.data.found) {
        setTaxpayerData(response.data.taxpayer);
        // Auto-fill form
        setInvoiceForm(prev => ({
          ...prev,
          doc_tipo: 'cuit',
          doc_nro: response.data.taxpayer.cuit
        }));
        toast.success(`Contribuyente encontrado: ${response.data.taxpayer.razon_social}`);
      } else {
        toast.error('No se encontró el contribuyente');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al buscar contribuyente');
    } finally {
      setLookingUp(false);
    }
  };
  
  // Download PDF
  const handleDownloadPdf = async (invoice) => {
    setDownloadingPdf(invoice.invoice_id);
    try {
      const response = await axios.get(
        `${API}/afip/invoice/${invoice.invoice_id}/pdf`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const pto = String(invoice.punto_vta).padStart(4, '0');
      const nro = String(invoice.cbte_nro).padStart(8, '0');
      link.setAttribute('download', `factura_${pto}_${nro}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al descargar PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };
  
  // Share invoice
  const handleShare = async (invoice) => {
    setLoadingShare(true);
    setShowShareDialog(true);
    try {
      const response = await axios.get(
        `${API}/afip/invoice/${invoice.invoice_id}/share`,
        { withCredentials: true }
      );
      setShareData(response.data);
    } catch (error) {
      toast.error('Error al obtener datos para compartir');
      setShowShareDialog(false);
    } finally {
      setLoadingShare(false);
    }
  };
  
  // Clear date filters
  const clearDateFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setPage(1);
  };
  
  // Create NC/ND from existing invoice
  const handleCreateNCFromInvoice = (invoice) => {
    setCreditNoteForm({
      importe_total: String(invoice.importe_total || ''),
      cbte_asoc_nro: String(invoice.cbte_nro || ''),
      motivo: ''
    });
    setShowDetailDialog(false);
    setShowCreditNoteDialog(true);
  };
  
  const handleCreateNDFromInvoice = (invoice) => {
    setDebitNoteForm({
      importe_total: '',
      cbte_asoc_nro: String(invoice.cbte_nro || ''),
      motivo: ''
    });
    setShowDetailDialog(false);
    setShowDebitNoteDialog(true);
  };
  
  // Reset taxpayer data when dialog closes
  const handleInvoiceDialogClose = (open) => {
    if (!open) {
      setTaxpayerData(null);
      setLookupCuit('');
    }
    setShowInvoiceDialog(open);
  };

  const getInvoiceTypeName = (tipo) => {
    switch(tipo) {
      case 11: return 'Factura C';
      case 12: return 'Nota de Crédito C';
      case 13: return 'Nota de Débito C';
      default: return tipo === 'credit_note' ? 'Nota de Crédito C' : tipo === 'debit_note' ? 'Nota de Débito C' : 'Factura C';
    }
  };

  const getInvoiceTypeColor = (tipo, tipoStr) => {
    if (tipo === 12 || tipoStr === 'credit_note') return 'bg-red-100 text-red-700';
    if (tipo === 13 || tipoStr === 'debit_note') return 'bg-orange-100 text-orange-700';
    return 'bg-green-100 text-green-700';
  };

  const getInvoiceTypeIcon = (tipo, tipoStr) => {
    if (tipo === 12 || tipoStr === 'credit_note') return <FileMinus className="w-4 h-4" />;
    if (tipo === 13 || tipoStr === 'debit_note') return <FilePlus className="w-4 h-4" />;
    return <FileCheck className="w-4 h-4" />;
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filterType === 'all') return true;
    if (filterType === 'facturas') return inv.cbte_tipo === 11 && !inv.tipo;
    if (filterType === 'nc') return inv.cbte_tipo === 12 || inv.tipo === 'credit_note';
    if (filterType === 'nd') return inv.cbte_tipo === 13 || inv.tipo === 'debit_note';
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    if (dateStr.length === 8) {
      return `${dateStr.slice(6,8)}/${dateStr.slice(4,6)}/${dateStr.slice(0,4)}`;
    }
    return new Date(dateStr).toLocaleDateString('es-AR');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Facturación Electrónica</h1>
                <p className="text-sm text-muted-foreground">
                  {afipStatus?.razon_social || 'Cargando...'} - CUIT: {afipStatus?.cuit || '...'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {afipStatus?.afip_status?.auth_server === 'OK' ? (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" /> AFIP Conectado
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <AlertCircle className="w-3 h-3 mr-1" /> Sin conexión
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Facturas</p>
                    <p className="text-2xl font-bold text-green-600">{summary.total_facturas}</p>
                    <p className="text-sm">${summary.monto_facturas?.toLocaleString()}</p>
                  </div>
                  <FileCheck className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Notas de Crédito</p>
                    <p className="text-2xl font-bold text-red-600">{summary.total_nc}</p>
                    <p className="text-sm">-${summary.monto_nc?.toLocaleString()}</p>
                  </div>
                  <FileMinus className="w-8 h-8 text-red-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Notas de Débito</p>
                    <p className="text-2xl font-bold text-orange-600">{summary.total_nd}</p>
                    <p className="text-sm">${summary.monto_nd?.toLocaleString()}</p>
                  </div>
                  <FilePlus className="w-8 h-8 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-primary/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Neto</p>
                    <p className="text-2xl font-bold">${summary.neto?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Facturado</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-primary opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button onClick={() => setShowInvoiceDialog(true)} className="bg-green-700 hover:bg-green-800">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Factura C
          </Button>
          <Button variant="outline" onClick={() => setShowCreditNoteDialog(true)} className="border-red-300 text-red-600 hover:bg-red-50">
            <FileMinus className="w-4 h-4 mr-2" />
            Nota de Crédito
          </Button>
          <Button variant="outline" onClick={() => setShowDebitNoteDialog(true)} className="border-orange-300 text-orange-700 hover:bg-orange-50">
            <FilePlus className="w-4 h-4 mr-2" />
            Nota de Débito
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => { fetchInvoices(); fetchSummary(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Invoices List */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <CardTitle>Comprobantes Emitidos</CardTitle>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="facturas">Facturas C</SelectItem>
                    <SelectItem value="nc">Notas de Crédito</SelectItem>
                    <SelectItem value="nd">Notas de Débito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <CalendarDays className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Filtrar por fecha:</span>
                
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-[130px] justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {dateFrom ? format(dateFrom, 'dd/MM/yyyy') : 'Desde'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={dateFrom}
                      onSelect={setDateFrom}
                      locale={es}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-[130px] justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {dateTo ? format(dateTo, 'dd/MM/yyyy') : 'Hasta'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={dateTo}
                      onSelect={setDateTo}
                      locale={es}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                
                {(dateFrom || dateTo) && (
                  <Button variant="ghost" size="sm" onClick={clearDateFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Limpiar
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Cargando comprobantes...</div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No hay comprobantes emitidos
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">Tipo</th>
                        <th className="pb-3 font-medium">Número</th>
                        <th className="pb-3 font-medium">Fecha</th>
                        <th className="pb-3 font-medium">Importe</th>
                        <th className="pb-3 font-medium">CAE</th>
                        <th className="pb-3 font-medium">Vto. CAE</th>
                        <th className="pb-3 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map((invoice) => (
                        <tr key={invoice.invoice_id} className="border-b hover:bg-muted/50">
                          <td className="py-3">
                            <Badge className={getInvoiceTypeColor(invoice.cbte_tipo, invoice.tipo)}>
                              {getInvoiceTypeIcon(invoice.cbte_tipo, invoice.tipo)}
                              <span className="ml-1">{getInvoiceTypeName(invoice.cbte_tipo || invoice.tipo)}</span>
                            </Badge>
                          </td>
                          <td className="py-3 font-mono">
                            {String(invoice.punto_vta).padStart(4, '0')}-{String(invoice.cbte_nro).padStart(8, '0')}
                          </td>
                          <td className="py-3">{formatDate(invoice.fecha)}</td>
                          <td className="py-3 font-medium">${invoice.importe_total?.toLocaleString()}</td>
                          <td className="py-3 font-mono text-xs">{invoice.cae}</td>
                          <td className="py-3">{formatDate(invoice.cae_vto)}</td>
                          <td className="py-3">
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => viewInvoiceDetail(invoice)} title="Ver detalle">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDownloadPdf(invoice)}
                                disabled={downloadingPdf === invoice.invoice_id}
                                title="Descargar PDF"
                              >
                                {downloadingPdf === invoice.invoice_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleShare(invoice)}
                                title="Compartir"
                              >
                                <Share2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Página {page} de {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog: Nueva Factura */}
      <Dialog open={showInvoiceDialog} onOpenChange={handleInvoiceDialogClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva Factura C</DialogTitle>
            <DialogDescription>Emitir una nueva factura electrónica</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* CUIT Lookup */}
            <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
              <Label className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                Buscar contribuyente por CUIT
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Ej: 20299507107"
                  value={lookupCuit}
                  onChange={(e) => setLookupCuit(e.target.value.replace(/\D/g, ''))}
                  maxLength={11}
                />
                <Button 
                  variant="secondary" 
                  onClick={handleLookupCuit}
                  disabled={lookingUp || lookupCuit.length < 11}
                >
                  {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {taxpayerData && (
                <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    {taxpayerData.razon_social}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400">
                    {taxpayerData.condicion_iva || 'Contribuyente'} - CUIT: {taxpayerData.cuit}
                  </p>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Importe Total *</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={invoiceForm.importe_total}
                onChange={(e) => setInvoiceForm({...invoiceForm, importe_total: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo Documento</Label>
                <Select 
                  value={invoiceForm.doc_tipo} 
                  onValueChange={(v) => {
                    setInvoiceForm({...invoiceForm, doc_tipo: v, doc_nro: v === 'cf' ? '' : invoiceForm.doc_nro});
                    if (v !== 'cuit') setTaxpayerData(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cf">Consumidor Final</SelectItem>
                    <SelectItem value="dni">DNI</SelectItem>
                    <SelectItem value="cuit">CUIT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nro. Documento</Label>
                <Input
                  placeholder={invoiceForm.doc_tipo === 'cf' ? 'No requerido' : 'Número'}
                  value={invoiceForm.doc_nro}
                  onChange={(e) => setInvoiceForm({...invoiceForm, doc_nro: e.target.value})}
                  disabled={invoiceForm.doc_tipo === 'cf'}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Concepto</Label>
              <Select 
                value={String(invoiceForm.concepto)} 
                onValueChange={(v) => setInvoiceForm({...invoiceForm, concepto: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Productos</SelectItem>
                  <SelectItem value="2">Servicios</SelectItem>
                  <SelectItem value="3">Productos y Servicios</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Textarea
                placeholder="Descripción del producto o servicio"
                value={invoiceForm.descripcion}
                onChange={(e) => setInvoiceForm({...invoiceForm, descripcion: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleInvoiceDialogClose(false)}>Cancelar</Button>
            <Button onClick={handleCreateInvoice} disabled={submitting} className="bg-green-700 hover:bg-green-800">
              {submitting ? 'Emitiendo...' : 'Emitir Factura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nota de Crédito */}
      <Dialog open={showCreditNoteDialog} onOpenChange={setShowCreditNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Nota de Crédito C</DialogTitle>
            <DialogDescription>Emitir una nota de crédito para anular o devolver</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nro. Factura Asociada *</Label>
              <Input
                type="number"
                placeholder="Número de la factura a anular/devolver"
                value={creditNoteForm.cbte_asoc_nro}
                onChange={(e) => setCreditNoteForm({...creditNoteForm, cbte_asoc_nro: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">Ingrese solo el número, sin el punto de venta</p>
            </div>
            <div className="space-y-2">
              <Label>Importe *</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={creditNoteForm.importe_total}
                onChange={(e) => setCreditNoteForm({...creditNoteForm, importe_total: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Motivo</Label>
              <Textarea
                placeholder="Motivo de la nota de crédito"
                value={creditNoteForm.motivo}
                onChange={(e) => setCreditNoteForm({...creditNoteForm, motivo: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditNoteDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateCreditNote} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? 'Emitiendo...' : 'Emitir NC'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nota de Débito */}
      <Dialog open={showDebitNoteDialog} onOpenChange={setShowDebitNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Nota de Débito C</DialogTitle>
            <DialogDescription>Emitir una nota de débito para ajustes adicionales</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nro. Factura Asociada *</Label>
              <Input
                type="number"
                placeholder="Número de la factura asociada"
                value={debitNoteForm.cbte_asoc_nro}
                onChange={(e) => setDebitNoteForm({...debitNoteForm, cbte_asoc_nro: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Importe *</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={debitNoteForm.importe_total}
                onChange={(e) => setDebitNoteForm({...debitNoteForm, importe_total: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Motivo</Label>
              <Textarea
                placeholder="Motivo de la nota de débito"
                value={debitNoteForm.motivo}
                onChange={(e) => setDebitNoteForm({...debitNoteForm, motivo: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDebitNoteDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateDebitNote} disabled={submitting} className="bg-orange-600 hover:bg-orange-700">
              {submitting ? 'Emitiendo...' : 'Emitir ND'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Detalle de Comprobante */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedInvoice && getInvoiceTypeName(selectedInvoice.cbte_tipo || selectedInvoice.tipo)}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Número</p>
                  <p className="font-mono font-medium">
                    {String(selectedInvoice.punto_vta).padStart(4, '0')}-{String(selectedInvoice.cbte_nro).padStart(8, '0')}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Fecha</p>
                  <p className="font-medium">{formatDate(selectedInvoice.fecha)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Importe Total</p>
                  <p className="font-medium text-lg">${selectedInvoice.importe_total?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Punto de Venta</p>
                  <p className="font-medium">{selectedInvoice.punto_vta}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-muted-foreground text-sm">CAE</p>
                <p className="font-mono text-lg">{selectedInvoice.cae}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Vencimiento: {formatDate(selectedInvoice.cae_vto)}
                </p>
              </div>
              
              {selectedInvoice.cbte_asoc_nro && (
                <div className="border-t pt-4">
                  <p className="text-muted-foreground text-sm">Comprobante Asociado</p>
                  <p className="font-mono">Factura C Nro: {selectedInvoice.cbte_asoc_nro}</p>
                </div>
              )}
              
              {selectedInvoice.motivo && (
                <div className="border-t pt-4">
                  <p className="text-muted-foreground text-sm">Motivo</p>
                  <p>{selectedInvoice.motivo}</p>
                </div>
              )}
              
              {selectedInvoice.qr_url && (
                <div className="border-t pt-4">
                  <a 
                    href={selectedInvoice.qr_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-sm"
                  >
                    Ver en AFIP →
                  </a>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleDownloadPdf(selectedInvoice)}
                    disabled={downloadingPdf === selectedInvoice.invoice_id}
                  >
                    {downloadingPdf === selectedInvoice.invoice_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Descargar PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleShare(selectedInvoice)}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Compartir
                  </Button>
                </div>
                
                {/* Only show NC/ND buttons for Facturas (tipo 11) */}
                {(selectedInvoice.cbte_tipo === 11 && !selectedInvoice.tipo) && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => handleCreateNCFromInvoice(selectedInvoice)}
                    >
                      <FileMinus className="w-4 h-4 mr-2" />
                      Emitir NC
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50"
                      onClick={() => handleCreateNDFromInvoice(selectedInvoice)}
                    >
                      <FilePlus className="w-4 h-4 mr-2" />
                      Emitir ND
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Dialog: Compartir Comprobante */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Compartir Comprobante</DialogTitle>
            <DialogDescription>Envía el comprobante por WhatsApp o Email</DialogDescription>
          </DialogHeader>
          {loadingShare ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground mt-2">Preparando datos...</p>
            </div>
          ) : shareData && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium">{shareData.tipo}</p>
                <p className="font-mono">N° {shareData.numero}</p>
                <p>Fecha: {shareData.fecha}</p>
                <p className="font-medium text-lg mt-1">${shareData.importe?.toLocaleString()}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <a
                  href={shareData.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp
                </a>
                <a
                  href={shareData.email_url}
                  className="flex items-center justify-center gap-2 p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Email
                </a>
              </div>
              
              <div className="border-t pt-4">
                <Label className="text-sm text-muted-foreground">Verificar en AFIP</Label>
                <a 
                  href={shareData.qr_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-1 text-primary hover:underline text-sm truncate"
                >
                  {shareData.qr_url}
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
